<script lang="ts" setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import axios from 'axios'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import {
  DCard,
  DBtn,
  DModal,
  DLoading,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  ArrowLeftIcon,
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  Save01Icon,
  PlusIcon,
  Lock01Icon,
  FileCode01Icon,
  EyeIcon,
  RefreshCw05Icon,
  Trash03Icon,
  Copy06Icon,
  LinkExternal01Icon
} from 'front-library'
import StateBadge from '../components/StateBadge.vue'
import YamlEditor from '../components/YamlEditor.vue'
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteDraft,
  publishTemplate,
  createWorkingVersion,
  validateYaml,
  dryRunTemplate,
  downloadDryRunFile,
  getEditorSupport,
  getTemplateDocumentation,
  getCountryOptions,
  getBankOptions,
  buildBaseYaml,
  FALLBACK_SUGGESTIONS,
  BankOption,
  BankTemplate,
  BankTemplateApiError,
  BankTemplateDocumentation,
  CanonicalSuggestion,
  DryRunResult,
  SelectOption,
  ValidationResult
} from '@/app/core/services/bank-templates'
import {
  BankTemplateWebMcpState,
  registerBankTemplateWebMcpTools
} from '@/app/core/services/bank-templates/webmcp'

const route = useRoute()
const router = useRouter()
const notifications = useNotificationsStore()
const { t, te } = useI18n()

const loading = ref(false)
const messageLoading = ref('')

/* Identidad y estado de la plantilla */
const templateId = ref('')
const active = ref(true)
const publishedVersion = ref<number | null>(null)
const hasDraft = ref(false)

/* Formulario basico (UUIDs reales de los catalogos) */
const name = ref('')
const countryId = ref('')
const originBankId = ref('')
const destinationBankIds = ref<string[]>([])

/* Contenido YAML en edicion (borrador) o en vista (publicada) */
const yaml = ref('')

const countries = ref<SelectOption[]>([])
const banks = ref<BankOption[]>([])
const suggestions = ref<CanonicalSuggestion[]>(FALLBACK_SUGGESTIONS)

/* Texto de busqueda de los selects (patron DSelectFieldSearch del sistema) */
const searchCountry = ref('')
const searchOriginBank = ref('')

const filterOptions = computed(
  () => (items: SelectOption[], term: string) =>
    items.filter(item =>
      item.name.toLowerCase().includes(term.toString().toLowerCase())
    )
)

const editorRef = ref<InstanceType<typeof YamlEditor> | null>(null)

/*
 * Modo del estudio:
 * - new: plantilla sin guardar → solo datos basicos + Guardar
 * - draft: hay borrador → se edita todo (datos basicos + YAML)
 * - published: solo hay version publicada → lectura + "Crear borrador"
 */
const isNew = computed(() => !templateId.value)
const editingDraft = computed(() => isNew.value || hasDraft.value)
const readonly = computed(() => !editingDraft.value)

const basicComplete = computed(
  () =>
    !!name.value.trim() &&
    !!countryId.value &&
    !!originBankId.value &&
    destinationBankIds.value.length > 0
)

/* Bancos del pais elegido (el catalogo ya viene filtrado por pais) */
const loadBanks = async () => {
  banks.value = countryId.value ? await getBankOptions(countryId.value) : []
}

/* Solo cuando el usuario cambia el pais: invalida los bancos elegidos */
const onCountryChange = async () => {
  originBankId.value = ''
  destinationBankIds.value = []
  await loadBanks()
}

/* -------------------------------------------------------------------------- */
/* Validacion en vivo (siempre en el backend, sin boton)                       */
/* -------------------------------------------------------------------------- */

const VALIDATE_DEBOUNCE_MS = 700

const validation = ref<ValidationResult | null>(null)
const validating = ref(false)
let validateDebounce: ReturnType<typeof setTimeout> | null = null
let validateController: AbortController | null = null
let validateSeq = 0

const runValidation = async () => {
  validateController?.abort()
  validateController = new AbortController()
  const seq = ++validateSeq
  try {
    const result = await validateYaml(yaml.value, validateController.signal)
    if (seq !== validateSeq) return
    validation.value = result
  } catch (error: any) {
    /* Cancelado por un tecleo mas reciente: se ignora */
    if (axios.isCancel(error) || error?.name === 'CanceledError') return
    if (seq !== validateSeq) return
    validation.value = {
      valid: false,
      issues: [{ severity: 'error', message: errorMessage(error) }]
    }
  } finally {
    if (seq === validateSeq) validating.value = false
  }
}

const scheduleValidation = () => {
  validating.value = true
  if (validateDebounce) clearTimeout(validateDebounce)
  validateDebounce = setTimeout(
    () => void runValidation(),
    VALIDATE_DEBOUNCE_MS
  )
}

/* Ultima validacion sin errores */
const templateValidated = computed(
  () => validation.value?.valid === true && !validating.value
)

const goToLine = (line: number) => {
  editorRef.value?.focusLine(line)
}

/* Los issues se muestran DENTRO del panel de vista previa */
const showIssues = computed(
  () =>
    editingDraft.value &&
    !validating.value &&
    validation.value !== null &&
    !validation.value.valid
)

/* -------------------------------------------------------------------------- */
/* Guardado automatico (despues del primer guardado)                           */
/* -------------------------------------------------------------------------- */

const AUTOSAVE_DEBOUNCE_MS = 1500

/* true cuando hay cambios sin persistir en el backend */
const dirty = ref(false)
const saving = ref(false)
/* Evita que volcar la plantilla del backend marque cambios */
let applying = false
let autosaveTimer: ReturnType<typeof setTimeout> | null = null

/*
 * Cada edicion (YAML o datos basicos) revalida y programa el guardado
 * automatico. Solo se persiste con el YAML valido y los datos completos,
 * porque el backend rechaza definiciones invalidas.
 */
const markDirty = (revalidate: boolean) => {
  if (applying || isNew.value || readonly.value) return
  dirty.value = true
  previewStale.value = preview.value !== null
  if (revalidate) scheduleValidation()
  scheduleAutosave()
}

const scheduleAutosave = () => {
  if (autosaveTimer) clearTimeout(autosaveTimer)
  autosaveTimer = setTimeout(() => void autosave(), AUTOSAVE_DEBOUNCE_MS)
}

const autosave = async () => {
  autosaveTimer = null
  if (!dirty.value || isNew.value || readonly.value) return
  /* Validacion aun en curso: se reintenta cuando termine */
  if (validating.value || saving.value) return scheduleAutosave()
  if (!templateValidated.value || !basicComplete.value) return
  /* Guardado: la vista previa se regenera sola sobre lo recien guardado */
  if (await persist({ silent: true })) void refreshPreview()
}

watch(yaml, () => markDirty(true))
watch([name, countryId, originBankId], () => markDirty(false))
watch(destinationBankIds, () => markDirty(false), { deep: true })

/* -------------------------------------------------------------------------- */
/* Vista previa: dry run del backend sobre lo guardado (+ descarga)            */
/* -------------------------------------------------------------------------- */

const preview = ref<DryRunResult | null>(null)
const loadingPreview = ref(false)
/* true cuando la plantilla cambio despues del ultimo dry run */
const previewStale = ref(false)
const previewError = ref('')

/*
 * Advertencias conocidas del dry run: el backend las envia como texto fijo en
 * ingles; se muestran en el idioma configurado. Las demas, tal cual llegan.
 */
const KNOWN_DRY_RUN_WARNINGS: Record<string, string> = {
  'dry-run output was produced with synthetic data':
    'bankTemplates.dryRun.syntheticData'
}

const translateWarning = (warning: string): string => {
  const key = KNOWN_DRY_RUN_WARNINGS[warning.trim()]
  return key ? t(key) : warning
}

/* Solo refresca el ejemplo (al abrir la plantilla), sin descargar */
const refreshPreview = async () => {
  if (isNew.value) return
  loadingPreview.value = true
  previewError.value = ''
  try {
    preview.value = await dryRunTemplate(templateId.value)
    previewStale.value = false
  } catch (error: any) {
    preview.value = null
    previewError.value = errorMessage(error)
  } finally {
    loadingPreview.value = false
  }
}

/* "Generar vista previa": guarda lo pendiente, genera el dry run y descarga */
const generatePreview = async () => {
  if (isNew.value || loadingPreview.value) return
  if (editingDraft.value && dirty.value) {
    if (!templateValidated.value || !basicComplete.value) {
      notifyInvalid()
      return
    }
    if (!(await persist({ silent: true }))) return
  }
  loadingPreview.value = true
  previewError.value = ''
  try {
    const result = await dryRunTemplate(templateId.value)
    preview.value = result
    previewStale.value = false
    downloadDryRunFile(result)
    notifications.addNotification({
      title: t('bankTemplates.notification.testFileTitle'),
      text: t('bankTemplates.notification.testFileText', {
        file: result.fileName
      }),
      type: 'success',
      time: 8000
    })
  } catch (error: any) {
    preview.value = null
    previewError.value = errorMessage(error)
  } finally {
    loadingPreview.value = false
  }
}

/* -------------------------------------------------------------------------- */
/* Documentacion                                                               */
/* -------------------------------------------------------------------------- */

const documentation = ref<BankTemplateDocumentation | null>(null)
const loadingDocumentation = ref(false)

const loadDocumentation = async (): Promise<BankTemplateDocumentation> => {
  if (documentation.value) return documentation.value
  loadingDocumentation.value = true
  try {
    documentation.value = await getTemplateDocumentation()
    return documentation.value
  } finally {
    loadingDocumentation.value = false
  }
}

const buildDocumentationForLlm = (docs: BankTemplateDocumentation): string => {
  const sections = docs.sections
    .map(section => `# ${section.title}\n\n${section.content.trim()}`)
    .join('\n\n---\n\n')
  const examples = docs.examples
    .map(
      example =>
        `# ${example.title}\n\nArchivo: ${
          example.fileName
        }\n\n\`\`\`yaml\n${example.content.trim()}\n\`\`\``
    )
    .join('\n\n---\n\n')
  return [
    'Usa esta documentacion para ayudarme a construir o ajustar una plantilla YAML bank-template. Usa solamente los campos, funciones DSL y ejemplos documentados aqui.',
    '',
    `# ${docs.title}`,
    '',
    sections,
    '',
    '---',
    '',
    '# Ejemplos YAML',
    '',
    examples
  ].join('\n')
}

const copyDocumentationForLlm = async () => {
  try {
    const docs = await loadDocumentation()
    await navigator.clipboard.writeText(buildDocumentationForLlm(docs))
    notifications.addNotification({
      title: t('bankTemplates.notification.documentationCopiedTitle'),
      text: t('bankTemplates.notification.documentationCopiedText'),
      type: 'success',
      time: 6000
    })
  } catch (error: any) {
    showError(error)
  }
}

const openDocumentationTab = async () => {
  const route = router.resolve({ name: 'BankTemplateDocumentation' })
  const tab = window.open(route.href, '_blank')
  if (!tab) {
    notifications.addNotification({
      title: t('bankTemplates.notification.documentationBlockedTitle'),
      text: t('bankTemplates.notification.documentationBlockedText'),
      type: 'warning',
      time: 8000
    })
    return
  }
  tab.opener = null
}

/* -------------------------------------------------------------------------- */
/* Acciones                                                                    */
/* -------------------------------------------------------------------------- */

const basicFormValid = (): boolean => {
  if (basicComplete.value) return true
  notifications.addNotification({
    title: t('bankTemplates.notification.basicRequiredTitle'),
    text: t('bankTemplates.notification.basicRequiredText'),
    type: 'warning',
    time: 8000
  })
  return false
}

const notifyInvalid = () => {
  notifications.addNotification({
    title: t('bankTemplates.notification.invalidTitle'),
    text: t('bankTemplates.notification.invalidText'),
    type: 'warning',
    time: 8000
  })
}

/*
 * Primer guardado: solo los datos basicos. La plantilla se crea con el YAML
 * de ejemplo y a partir de ahi se muestra el editor y la vista previa.
 */
const create = async () => {
  if (!basicFormValid()) return
  loading.value = true
  messageLoading.value = t('bankTemplates.loading.saving')
  try {
    const id = await createTemplate({
      name: name.value.trim(),
      countryId: countryId.value,
      originBankId: originBankId.value,
      destinationBankIds: destinationBankIds.value,
      yaml: buildBaseYaml()
    })
    void router.replace(`/bank-template/${id}`)
    applyTemplate(await getTemplate(id))
    scheduleValidation()
    notifications.addNotification({
      title: t('bankTemplates.notification.createdTitle'),
      text: t('bankTemplates.notification.createdText'),
      type: 'success',
      time: 6000
    })
    void refreshPreview()
  } catch (error: any) {
    showError(error)
  } finally {
    loading.value = false
  }
}

/* Persiste datos basicos + YAML del borrador en un solo PUT */
const persist = async ({ silent }: { silent: boolean }): Promise<boolean> => {
  if (isNew.value || readonly.value) return false
  if (!silent && !basicFormValid()) return false
  saving.value = true
  if (!silent) {
    loading.value = true
    messageLoading.value = t('bankTemplates.loading.saving')
  }
  /* Cambios que lleguen mientras se guarda vuelven a marcar dirty */
  dirty.value = false
  try {
    await updateTemplate(templateId.value, {
      name: name.value.trim(),
      countryId: countryId.value,
      originBankId: originBankId.value,
      destinationBankIds: destinationBankIds.value,
      yaml: yaml.value
    })
    hasDraft.value = true
    if (!silent) {
      notifications.addNotification({
        title: t('bankTemplates.notification.savedTitle'),
        text: t('bankTemplates.notification.savedText'),
        type: 'success',
        time: 6000
      })
    }
    return true
  } catch (error: any) {
    dirty.value = true
    showError(error)
    return false
  } finally {
    saving.value = false
    if (!silent) loading.value = false
  }
}

const save = async () => {
  if (autosaveTimer) clearTimeout(autosaveTimer)
  if (await persist({ silent: false })) void refreshPreview()
}

/* Publicar: con la validacion en verde; lo pendiente se guarda antes */
const showPublishModal = ref(false)

const nextVersion = computed(() => (publishedVersion.value ?? 0) + 1)

const canPublish = computed(
  () => templateValidated.value && !isNew.value && basicComplete.value
)

const askPublish = () => {
  if (!canPublish.value) return
  showPublishModal.value = true
}

const publish = async () => {
  if (!canPublish.value) return
  showPublishModal.value = false
  if (autosaveTimer) clearTimeout(autosaveTimer)
  if (dirty.value && !(await persist({ silent: true }))) return
  loading.value = true
  messageLoading.value = t('bankTemplates.loading.publishing')
  try {
    await publishTemplate(templateId.value)
    notifications.addNotification({
      title: t('bankTemplates.notification.publishedTitle'),
      text: t('bankTemplates.notification.publishedText', {
        name: name.value,
        version: nextVersion.value
      }),
      type: 'success',
      time: 8000
    })
    /* Publicada: se regresa al listado de plantillas */
    void router.push('/bank-templates')
  } catch (error: any) {
    showError(error)
    /* El backend re-valida al publicar: se refresca el detalle de issues */
    scheduleValidation()
  } finally {
    loading.value = false
  }
}

/* Crear borrador desde la version publicada (vista de solo lectura) */
const startDraft = async () => {
  loading.value = true
  messageLoading.value = t('bankTemplates.loading.saving')
  try {
    await createWorkingVersion(templateId.value)
    applyTemplate(await getTemplate(templateId.value))
    notifications.addNotification({
      title: t('bankTemplates.notification.draftCreatedTitle'),
      text: t('bankTemplates.notification.draftCreatedText'),
      type: 'success',
      time: 6000
    })
    scheduleValidation()
  } catch (error: any) {
    showError(error)
  } finally {
    loading.value = false
  }
}

/* Eliminar el borrador en curso */
const showDeleteDraftModal = ref(false)

const removeDraft = async () => {
  showDeleteDraftModal.value = false
  if (autosaveTimer) clearTimeout(autosaveTimer)
  loading.value = true
  messageLoading.value = t('bankTemplates.loading.saving')
  try {
    await deleteDraft(templateId.value)
    notifications.addNotification({
      title: t('bankTemplates.notification.draftDeletedTitle'),
      text: t('bankTemplates.notification.draftDeletedText'),
      type: 'success',
      time: 6000
    })
    if (publishedVersion.value === null) {
      /* Nunca publicada: no queda nada que mostrar */
      void router.push('/bank-templates')
      return
    }
    applyTemplate(await getTemplate(templateId.value))
  } catch (error: any) {
    showError(error)
  } finally {
    loading.value = false
  }
}

const goBack = () => {
  void router.push('/bank-templates')
}

/* Vuelca la plantilla del servicio al estado local del estudio */
const applyTemplate = (template: BankTemplate) => {
  applying = true
  templateId.value = template.id
  name.value = template.name
  countryId.value = template.countryId
  originBankId.value = template.originBankId
  destinationBankIds.value = [...template.destinationBankIds]
  active.value = template.active
  publishedVersion.value = template.publishedVersion
  hasDraft.value = template.draftYaml !== null
  yaml.value = template.draftYaml ?? template.publishedYaml ?? ''
  validation.value = null
  dirty.value = false
  /* Los watchers corren en el siguiente flush: se libera despues */
  void nextTick(() => (applying = false))
}

/*
 * Mensaje en el idioma configurado: si el backend envia un codigo de dominio
 * con traduccion se usa esa; si no, el mensaje original.
 */
const errorMessage = (error: any): string => {
  if (error instanceof BankTemplateApiError && error.code) {
    const key = `bankTemplates.errors.${error.code}`
    if (te(key)) return t(key, error.params ?? {})
  }
  return error?.message ?? String(error)
}

const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: 'Error',
    text: errorMessage(error),
    type: 'error',
    time: 10000
  })
}

/* -------------------------------------------------------------------------- */
/* WebMCP: herramientas para agentes en Chrome                                 */
/* -------------------------------------------------------------------------- */

let cleanupWebMcpTools: (() => void) | null = null

const getWebMcpMode = (): BankTemplateWebMcpState['mode'] => {
  if (isNew.value) return 'new'
  return editingDraft.value ? 'draft' : 'published'
}

const getWebMcpState = (): BankTemplateWebMcpState => ({
  templateId: templateId.value,
  mode: getWebMcpMode(),
  readonly: readonly.value,
  name: name.value,
  countryId: countryId.value,
  originBankId: originBankId.value,
  destinationBankIds: [...destinationBankIds.value],
  yaml: yaml.value,
  validation: validation.value,
  validating: validating.value,
  dirty: dirty.value,
  destinationsChanged: dirty.value,
  canPublish: canPublish.value,
  suggestions: suggestions.value
})

const setYamlFromWebMcp = async (value: string) => {
  yaml.value = value
  await nextTick()
}

const validateYamlFromWebMcp = async (
  candidate?: string,
  signal?: AbortSignal
): Promise<ValidationResult> => {
  const target = candidate ?? yaml.value
  const updatesCurrentEditor = target === yaml.value
  if (updatesCurrentEditor) {
    if (validateDebounce) clearTimeout(validateDebounce)
    validateController?.abort()
    validateController = null
    validating.value = true
  }
  try {
    const result = await validateYaml(target, signal)
    if (updatesCurrentEditor) validation.value = result
    return result
  } finally {
    if (updatesCurrentEditor) validating.value = false
  }
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('bankTemplates.loading.data')
  try {
    countries.value = await getCountryOptions()
    /* Sugerencias reales del backend; si falla se queda el fallback local */
    getEditorSupport()
      .then(support => (suggestions.value = support.suggestions))
      .catch(() => undefined)
    const id = route.params.id as string | undefined
    if (id) {
      applyTemplate(await getTemplate(id))
      await loadBanks()
      if (editingDraft.value) scheduleValidation()
      void refreshPreview()
    }
    cleanupWebMcpTools = registerBankTemplateWebMcpTools({
      getState: getWebMcpState,
      setYaml: setYamlFromWebMcp,
      validateYaml: validateYamlFromWebMcp
    })
  } catch (error: any) {
    showError(error)
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  if (validateDebounce) clearTimeout(validateDebounce)
  if (autosaveTimer) clearTimeout(autosaveTimer)
  validateController?.abort()
  cleanupWebMcpTools?.()
})
</script>
<template>
  <div class="h-full px-5 pb-5 overflow-auto scroll">
    <DLoading v-show="loading" :message="messageLoading" />

    <!-- Modal: publicar -->
    <DModal
      :open="showPublishModal"
      :form="true"
      :acceptQuestion="t('bankTemplates.publish.confirm')"
      @closeModal="showPublishModal = false"
      @otherMethod="publish">
      <template #form>
        <div class="w-full sm:w-9/12 px-2">
          <h3 class="text-xl font-semibold text-gray-900 mb-2">
            {{ t('bankTemplates.publish.title') }}
          </h3>
          <p class="text-sm text-gray-500 mb-4">
            {{ t('bankTemplates.publish.warning', { version: nextVersion }) }}
          </p>
          <div
            v-if="!templateValidated"
            class="flex items-start gap-2 bg-red-50 rounded-lg px-3 py-2 mb-2">
            <AlertCircleIcon class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <p class="text-sm text-red-600 pb-1">
              {{ t('bankTemplates.publish.invalid') }}
            </p>
          </div>
          <div
            v-else
            class="flex items-start gap-2 bg-green-50 rounded-lg px-3 py-2 mb-2">
            <CheckCircleIcon class="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
            <p class="text-sm text-green-700 pb-1">
              {{ t('bankTemplates.publish.valid') }}
            </p>
          </div>
        </div>
      </template>
    </DModal>

    <!-- Modal: eliminar borrador -->
    <DModal
      :open="showDeleteDraftModal"
      :typeAlert="'question'"
      :title="t('bankTemplates.deleteDraft.title')"
      :description="t('bankTemplates.deleteDraft.description')"
      :acceptQuestion="t('bankTemplates.studio.actions.deleteDraft')"
      @closeModal="showDeleteDraftModal = false"
      @otherMethod="removeDraft" />

    <!-- Header -->
    <div class="bg-gray-50 pt-responsive pb-4">
      <div class="flex items-center gap-3 flex-wrap">
        <p class="text-3xl font-semibold text-shadow text-gray-900 truncate">
          {{ name || t('bankTemplates.studio.untitled') }}
        </p>
        <StateBadge
          v-if="publishedVersion !== null"
          color="green"
          :label="
            t('bankTemplates.badges.version', { version: publishedVersion })
          " />
        <StateBadge
          v-if="!isNew && editingDraft"
          color="yellow"
          :label="t('bankTemplates.badges.draft')" />
        <StateBadge
          v-if="!active"
          color="gray"
          :label="t('bankTemplates.badges.inactive')" />
      </div>
    </div>

    <!-- Aviso de solo lectura (viendo la version publicada) -->
    <div
      v-if="readonly"
      class="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-3 text-sm text-blue-700">
      <Lock01Icon class="w-4 h-4 shrink-0" />
      {{ t('bankTemplates.studio.publishedReadonly') }}
    </div>

    <DCard>
      <div class="px-3 py-2">
        <!-- Parte 1: datos basicos (siempre visibles) -->
        <div
          class="flex flex-wrap gap-3 items-end"
          :class="isNew ? '' : 'pb-4 mb-4 border-b border-gray-100'">
          <DTextField
            class="w-full sm:w-64"
            v-model="name"
            :label="t('bankTemplates.overview.name')"
            :placeholder="t('bankTemplates.overview.namePlaceholder')"
            :required="true"
            :disabled="readonly" />
          <DSelectFieldSearch
            class="w-full sm:w-56"
            v-model="countryId"
            :label="t('bankTemplates.overview.country')"
            :options="filterOptions(countries, searchCountry)"
            :required="true"
            :disabled="readonly"
            @filter="searchCountry = $event"
            @change="onCountryChange" />
          <DSelectFieldSearch
            class="w-full sm:w-56"
            v-model="originBankId"
            :label="t('bankTemplates.overview.originBank')"
            :options="filterOptions(banks, searchOriginBank)"
            :required="true"
            :disabled="readonly || !countryId"
            @filter="searchOriginBank = $event" />
          <DMultiselect
            class="w-full sm:w-64"
            v-model="destinationBankIds"
            :label="t('bankTemplates.overview.destinationBanks')"
            :options="banks"
            value-key="id"
            :required="true"
            :disabled="readonly || !countryId"
            :header-title="t('bankTemplates.overview.destinationBanks')" />
        </div>

        <!-- Creacion: solo los datos basicos y Guardar -->
        <div v-if="isNew" class="flex justify-end items-center gap-2 mt-6">
          <DBtn
            type="button"
            color="secondary"
            class="px-4 py-2.5 font-medium flex items-center gap-2"
            @click.prevent="goBack">
            <ArrowLeftIcon class="w-4 h-4" />
            {{ t('bankTemplates.studio.actions.back') }}
          </DBtn>
          <DBtn
            type="button"
            class="px-5 py-2.5 font-semibold flex items-center gap-2"
            @click.prevent="create">
            <Save01Icon class="w-4 h-4" />
            {{ t('bankTemplates.studio.actions.save') }}
          </DBtn>
        </div>

        <!-- Parte 2: editor YAML + vista previa (despues del primer guardado) -->
        <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <!-- Mitad izquierda: editor -->
          <div class="min-w-0">
            <div class="flex items-start justify-between gap-3 mb-1">
              <h3
                class="text-base font-semibold text-gray-900 flex items-center gap-2">
                <FileCode01Icon class="w-5 h-5 text-gray-400" />
                {{ t('bankTemplates.editor.title') }}
              </h3>
              <div class="flex items-center gap-2 flex-wrap justify-end">
                <button
                  type="button"
                  class="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                  :disabled="loadingDocumentation"
                  @click="copyDocumentationForLlm">
                  <Copy06Icon class="w-4 h-4" />
                  {{ t('bankTemplates.documentation.copyForLlm') }}
                </button>
                <button
                  type="button"
                  class="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                  :disabled="loadingDocumentation"
                  @click="openDocumentationTab">
                  <LinkExternal01Icon class="w-4 h-4" />
                  {{ t('bankTemplates.documentation.open') }}
                </button>
              </div>
            </div>
            <p class="text-sm text-gray-500 mb-3">
              {{ t('bankTemplates.editor.hint') }}
            </p>
            <YamlEditor
              ref="editorRef"
              :modelValue="yaml"
              :suggestions="suggestions"
              :readonly="readonly"
              height="560px"
              @update:modelValue="yaml = $event" />
          </div>

          <!-- Mitad derecha: dry run del backend sobre lo guardado -->
          <div class="min-w-0 flex flex-col">
            <div class="flex items-center justify-between gap-3 mb-1">
              <h3
                class="text-base font-semibold text-gray-900 flex items-center gap-2">
                <EyeIcon class="w-5 h-5 text-gray-400" />
                {{ t('bankTemplates.editor.previewTitle') }}
              </h3>
              <!-- Genera el dry run y descarga el archivo de prueba -->
              <button
                type="button"
                class="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:text-gray-300"
                :disabled="loadingPreview || (editingDraft && !templateValidated)"
                @click="generatePreview">
                <RefreshCw05Icon
                  class="w-4 h-4"
                  :class="loadingPreview ? 'animate-spin' : ''" />
                {{
                  loadingPreview
                    ? t('bankTemplates.dryRun.running')
                    : t('bankTemplates.editor.previewRefresh')
                }}
              </button>
            </div>
            <p class="text-sm text-gray-500 mb-3">
              {{ t('bankTemplates.editor.previewNote') }}
            </p>
            <div
              class="border border-gray-200 rounded-xl bg-white overflow-hidden flex flex-col"
              style="height: 560px">
              <!-- Estado de la validacion en vivo (dentro de la vista previa) -->
              <div
                v-if="editingDraft && (validating || validation)"
                class="flex items-center gap-2 px-4 py-2 border-b border-gray-100"
                :class="
                  validating
                    ? 'bg-gray-50'
                    : validation?.valid
                    ? 'bg-green-50'
                    : 'bg-red-50'
                ">
                <template v-if="validating">
                  <RefreshCw05Icon
                    class="w-4 h-4 text-gray-400 shrink-0 animate-spin" />
                  <p class="text-xs text-gray-500">
                    {{ t('bankTemplates.validation.validating') }}
                  </p>
                </template>
                <template v-else-if="validation?.valid">
                  <CheckCircleIcon class="w-4 h-4 text-green-500 shrink-0" />
                  <p class="text-xs font-medium text-green-700">
                    {{ t('bankTemplates.validation.allGood') }}
                  </p>
                </template>
                <template v-else>
                  <AlertCircleIcon class="w-4 h-4 text-red-500 shrink-0" />
                  <p class="text-xs font-medium text-red-600">
                    {{ t('bankTemplates.validation.title') }}
                  </p>
                </template>
                <!-- Estado del guardado automatico -->
                <span
                  v-if="saving || dirty"
                  class="ml-auto text-xs text-gray-400 shrink-0">
                  {{
                    saving
                      ? t('bankTemplates.autosave.saving')
                      : t('bankTemplates.autosave.pending')
                  }}
                </span>
              </div>
              <!-- Nombre del archivo generado -->
              <div
                v-if="preview && !showIssues"
                class="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100 bg-gray-50">
                <span class="text-xs text-gray-400 shrink-0">
                  {{ t('bankTemplates.editor.previewFile') }}
                </span>
                <span class="text-xs font-mono text-gray-700 truncate">
                  {{ preview.fileName }}
                </span>
                <span
                  v-if="previewStale"
                  class="ml-auto text-xs text-amber-600 shrink-0">
                  {{ t('bankTemplates.editor.previewStale') }}
                </span>
              </div>
              <!-- Contenido -->
              <div class="flex-1 overflow-auto scroll p-4">
                <!-- Issues de la validacion, en el lugar del ejemplo -->
                <ul v-if="showIssues" class="flex flex-col gap-2">
                  <li
                    v-for="(issue, index) in validation?.issues"
                    :key="index"
                    class="flex items-start gap-2 text-sm">
                    <AlertCircleIcon
                      v-if="issue.severity === 'error'"
                      class="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                    <AlertTriangleIcon
                      v-else
                      class="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <span
                      :class="
                        issue.severity === 'error'
                          ? 'text-red-600'
                          : 'text-amber-600'
                      ">
                      {{ issue.message }}
                    </span>
                    <button
                      v-if="issue.line"
                      type="button"
                      class="text-blue-600 font-semibold hover:text-blue-700 text-xs shrink-0 mt-0.5"
                      @click="goToLine(issue.line)">
                      {{
                        t('bankTemplates.validation.goToLine', {
                          line: issue.line
                        })
                      }}
                    </button>
                  </li>
                </ul>
                <pre
                  v-else-if="preview && preview.encoding === 'utf-8'"
                  class="text-xs text-gray-800 font-mono leading-relaxed"
                  >{{ preview.lines.join('\n') }}</pre
                >
                <div
                  v-else-if="preview"
                  class="h-full flex flex-col items-center justify-center text-center px-6">
                  <FileCode01Icon class="w-8 h-8 text-gray-300 mb-3" />
                  <p class="text-sm text-gray-500 max-w-xs">
                    {{ t('bankTemplates.editor.previewBinary') }}
                  </p>
                </div>
                <div
                  v-else
                  class="h-full flex flex-col items-center justify-center text-center px-6">
                  <AlertTriangleIcon class="w-8 h-8 text-amber-400 mb-3" />
                  <p class="text-sm text-gray-500 max-w-xs">
                    {{ t('bankTemplates.editor.previewInvalid') }}
                  </p>
                  <p
                    v-if="previewError"
                    class="text-xs text-red-500 font-mono mt-2 max-w-sm break-words">
                    {{ previewError }}
                  </p>
                </div>
              </div>
              <!-- Advertencias del dry run -->
              <div
                v-if="preview && preview.warnings.length > 0"
                class="border-t border-gray-100 bg-amber-50 px-4 py-2">
                <p
                  v-for="(warning, index) in preview.warnings"
                  :key="index"
                  class="text-xs text-amber-700">
                  {{ translateWarning(warning) }}
                </p>
              </div>
            </div>

            <!-- Acciones: inferior derecha, debajo del ejemplo generado -->
            <div class="flex justify-end items-center gap-2 mt-4">
              <DBtn
                type="button"
                color="secondary"
                class="px-4 py-2.5 font-medium flex items-center gap-2"
                @click.prevent="goBack">
                <ArrowLeftIcon class="w-4 h-4" />
                {{ t('bankTemplates.studio.actions.back') }}
              </DBtn>
              <template v-if="editingDraft">
                <DBtn
                  type="button"
                  color="secondary"
                  class="px-4 py-2.5 font-medium flex items-center gap-2"
                  @click.prevent="showDeleteDraftModal = true">
                  <Trash03Icon class="w-4 h-4" />
                  {{ t('bankTemplates.studio.actions.deleteDraft') }}
                </DBtn>
                <DBtn
                  type="button"
                  color="secondary"
                  class="px-4 py-2.5 font-medium flex items-center gap-2"
                  :disabled="saving"
                  @click.prevent="save">
                  <Save01Icon class="w-4 h-4" />
                  {{ t('bankTemplates.studio.actions.save') }}
                </DBtn>
                <!-- Solo exige el YAML valido: lo pendiente se guarda al publicar -->
                <DBtn
                  type="button"
                  class="px-5 py-2.5 font-semibold flex items-center gap-2"
                  :disabled="!canPublish"
                  @click.prevent="askPublish">
                  {{ t('bankTemplates.studio.actions.publish') }}
                </DBtn>
              </template>
              <DBtn
                v-else
                type="button"
                class="px-5 py-2.5 font-semibold flex items-center gap-2"
                @click.prevent="startDraft">
                <PlusIcon class="w-4 h-4" />
                {{ t('bankTemplates.studio.actions.createDraft') }}
              </DBtn>
            </div>
          </div>
        </div>
      </div>
    </DCard>
  </div>
</template>
